
// Input validation and sanitization utilities for security
import DOMPurify from 'dompurify';

interface ValidationRule {
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  errorMessage: string;
}

export const ValidationRules: Record<string, ValidationRule> = {
  title: {
    minLength: 1,
    maxLength: 200,
    pattern: /^[a-zA-Z0-9\s\-_.,!?áéíóúàèìòùãõâêîôûç]*$/,
    errorMessage: "Título deve ter entre 1 e 200 caracteres e conter apenas letras, números e pontuação básica"
  },
  description: {
    minLength: 1,
    maxLength: 2000,
    pattern: /^[a-zA-Z0-9\s\-_.,!?\n\ráéíóúàèìòùãõâêîôûç]*$/,
    errorMessage: "Descrição deve ter entre 1 e 2000 caracteres"
  },
  email: {
    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    errorMessage: "Email deve ter um formato válido"
  },
  phone: {
    pattern: /^(\+55\s?)?\(?[1-9]{2}\)?\s?9?[0-9]{4}-?[0-9]{4}$/,
    errorMessage: "Telefone deve ter um formato válido (ex: (11) 99999-9999)"
  },
  name: {
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-ZáéíóúàèìòùãõâêîôûçÁÉÍÓÚÀÈÌÒÙÃÕÂÊÎÔÛÇ\s]*$/,
    errorMessage: "Nome deve ter entre 2 e 100 caracteres e conter apenas letras"
  },
  unitNumber: {
    pattern: /^[0-9A-Za-z-]{1,10}$/,
    errorMessage: "Número da unidade deve ter formato válido"
  },
  password: {
    minLength: 6,
    maxLength: 100,
    errorMessage: "Senha deve ter entre 6 e 100 caracteres"
  }
};

export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  // First sanitize with DOMPurify to remove XSS
  const purified = DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
  
  // Additional sanitization for database safety
  return purified
    .trim()
    .replace(/[<>\"'&]/g, '') // Remove potentially dangerous characters
    .substring(0, 2000); // Enforce max length
};

export const validateField = (value: string, fieldType: keyof typeof ValidationRules): {
  isValid: boolean;
  error?: string;
} => {
  const rules = ValidationRules[fieldType];
  
  if (!value || value.trim() === '') {
    return { isValid: false, error: "Este campo é obrigatório" };
  }

  const sanitizedValue = sanitizeInput(value);
  
  if (rules.minLength !== undefined && sanitizedValue.length < rules.minLength) {
    return { isValid: false, error: rules.errorMessage };
  }
  
  if (rules.maxLength !== undefined && sanitizedValue.length > rules.maxLength) {
    return { isValid: false, error: rules.errorMessage };
  }
  
  if (rules.pattern && !rules.pattern.test(sanitizedValue)) {
    return { isValid: false, error: rules.errorMessage };
  }
  
  return { isValid: true };
};

export const validatePassword = (password: string): {
  isValid: boolean;
  error?: string;
} => {
  if (!password) {
    return { isValid: false, error: "Senha é obrigatória" };
  }
  
  if (password.length < 8) {
    return { isValid: false, error: "Senha deve ter pelo menos 8 caracteres" };
  }
  
  if (!/(?=.*[a-z])/.test(password)) {
    return { isValid: false, error: "Senha deve conter pelo menos uma letra minúscula" };
  }
  
  if (!/(?=.*[A-Z])/.test(password)) {
    return { isValid: false, error: "Senha deve conter pelo menos uma letra maiúscula" };
  }
  
  if (!/(?=.*\d)/.test(password)) {
    return { isValid: false, error: "Senha deve conter pelo menos um número" };
  }
  
  if (!/(?=.*[!@#$%^&*])/.test(password)) {
    return { isValid: false, error: "Senha deve conter pelo menos um caractere especial (!@#$%^&*)" };
  }
  
  return { isValid: true };
};

// Rate limiting utility for authentication attempts
class RateLimiter {
  private attempts: Map<string, { count: number; lastAttempt: number }> = new Map();
  private maxAttempts = 5;
  private windowMs = 15 * 60 * 1000; // 15 minutes

  canAttempt(identifier: string): boolean {
    const now = Date.now();
    const userAttempts = this.attempts.get(identifier);
    
    if (!userAttempts) {
      return true;
    }
    
    // Reset if window has passed
    if (now - userAttempts.lastAttempt > this.windowMs) {
      this.attempts.delete(identifier);
      return true;
    }
    
    return userAttempts.count < this.maxAttempts;
  }
  
  recordAttempt(identifier: string): void {
    const now = Date.now();
    const userAttempts = this.attempts.get(identifier);
    
    if (!userAttempts || now - userAttempts.lastAttempt > this.windowMs) {
      this.attempts.set(identifier, { count: 1, lastAttempt: now });
    } else {
      userAttempts.count++;
      userAttempts.lastAttempt = now;
    }
  }
  
  getRemainingTime(identifier: string): number {
    const userAttempts = this.attempts.get(identifier);
    if (!userAttempts) return 0;
    
    const timePassed = Date.now() - userAttempts.lastAttempt;
    return Math.max(0, this.windowMs - timePassed);
  }
}

export const authRateLimiter = new RateLimiter();
